import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Brain, GitBranch, Lock, Minimize2, Server, SlidersHorizontal } from "lucide-react";
import { ServersDialog } from "../components/servers/ServersDialog";
import { CredentialsBoxDialog } from "../components/servers/CredentialsBoxDialog";
import { AppShell } from "../components/layout/AppShell";
import { ConversationList } from "../components/layout/ConversationList";
import { DeleteConversationDialog, PurgeConversationDialog } from "../components/layout/ConversationDialogs";
import { ChatWindow } from "../components/chat/ChatWindow";
import { ProjectWelcome } from "../components/chat/ProjectWelcome";
import { MemoryDialog } from "../components/chat/MemoryDialog";
import { GeneralPromptDialog } from "../components/chat/GeneralPromptDialog";
import { PreviewButton } from "../components/chat/PreviewButton";
import { PlanButton } from "../components/chat/PlanPanel";
import { PreviewHost } from "../components/chat/PreviewWindow";
import { DeployButton, DeployHost } from "../components/deploy/DeployDialog";
import { useServerStatus } from "../hooks/useServerStatus";
import { MessageInput } from "../components/chat/MessageInput";
import { NewTopicDialog } from "../components/chat/NewTopicDialog";
import { CompactChatDialog } from "../components/chat/CompactChatDialog";
import { ContextUsageBadge } from "../components/chat/ContextUsageBadge";
import { Button } from "../components/ui/button";
import { apiClient } from "../lib/api";
import { useAuth } from "../hooks/useAuth";
import { useProjects, useUpdateProject } from "../hooks/useProjects";
import { useConversations, useDeletedConversations, useMakeMain, useMessages, usePurgeConversation, useRestoreConversation, useTrashConversation } from "../hooks/useConversations";
import { errorMessage } from "../hooks/useServers";
import { useSendMessage, useChatProgress, useRunningJob, cancelJob, newRequestId, looksLikeCredentials } from "../hooks/useChat";
import { useContextUsage, useConversationSpend, useSetContextUsage } from "../hooks/useContextUsage";
import { useVersion } from "../hooks/useVersion";
import { useReadiness } from "../hooks/useDiscovery";

// El chat se monta de nuevo (con TODO su estado en blanco) cada vez que cambia el proyecto: así una conversación,
// una respuesta tardía o un borrador del proyecto anterior nunca aparecen bajo la cabecera del nuevo.
export default function ChatPage() {
  const { projectId } = useParams();
  return <ChatPageForProject key={projectId} selectedProjectId={projectId} />;
}

function ChatPageForProject({ selectedProjectId }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const queryClient = useQueryClient();
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [showTopicDialog, setShowTopicDialog] = useState(false);
  const [showCompactDialog, setShowCompactDialog] = useState(false);
  const [showServersDialog, setShowServersDialog] = useState(false);
  const [showCredentialsBox, setShowCredentialsBox] = useState(false);
  const [showMemory, setShowMemory] = useState(false);
  const [showGeneralPrompt, setShowGeneralPrompt] = useState(false);
  const [sendError, setSendError] = useState(false); // la respuesta no llegó a la pantalla (conexión cortada); se sigue buscando
  const sentCountRef = useRef(0); // cuántos mensajes había al enviar
  const [pendingUser, setPendingUser] = useState(null); // tu mensaje, mostrado al instante mientras el servidor lo guarda
  const [requestId, setRequestId] = useState(null); // código del envío en curso, para ver el avance de la IA
  const [startedAt, setStartedAt] = useState(null);
  const [jobRunning, setJobRunning] = useState(false); // la IA sigue trabajando en segundo plano (la respuesta aparecerá sola)
  const jobConvRef = useRef(null); // conversación a la que pertenece la tarea en curso
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const { data: projects } = useProjects();
  const { data: conversations, refetch: refetchConversations } = useConversations(selectedProjectId);
  const { data: deletedConversations } = useDeletedConversations(selectedProjectId);
  const makeMain = useMakeMain(selectedProjectId);
  const trashConversation = useTrashConversation(selectedProjectId);
  const restoreConversation = useRestoreConversation(selectedProjectId);
  const purgeConversation = usePurgeConversation(selectedProjectId);
  const [deleteTarget, setDeleteTarget] = useState(null); // chat que se está por eliminar (pide confirmación)
  const [purgeTarget, setPurgeTarget] = useState(null); // chat eliminado que se está por borrar de verdad
  const [dialogError, setDialogError] = useState("");
  const [chatsError, setChatsError] = useState("");
  const sendMessage = useSendMessage();
  const waiting = sendMessage.isPending || sendError || jobRunning;
  const { data: messages, refetch: refetchMessages } = useMessages(selectedConversationId, waiting ? 4000 : false);
  const { data: progress } = useChatProgress(requestId, sendMessage.isPending || jobRunning);
  const { data: runningJob } = useRunningJob(selectedConversationId, jobRunning);
  // Al abrir o recargar una conversación con una tarea en curso, la pantalla se vuelve a enganchar a ella; si dejó de estar en curso, se refresca.
  useEffect(() => {
    if (runningJob?.running && !jobRunning && !sendMessage.isPending) {
      setRequestId(runningJob.request_id);
      setStartedAt(runningJob.started_at ? Date.parse(runningJob.started_at) : Date.now());
      jobConvRef.current = selectedConversationId;
      setJobRunning(true);
    } else if (runningJob && !runningJob.running && jobRunning && !sendMessage.isPending) {
      setJobRunning(false);
      refetchMessages();
      queryClient.invalidateQueries({ queryKey: ["context-usage", selectedConversationId] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runningJob]);
  // Tu mensaje se ve al instante; en cuanto el servidor lo guarda (o llega la respuesta) lo reemplaza el mensaje real.
  const showPending =
    !!pendingUser && pendingUser.conversationId === selectedConversationId && (messages?.length || 0) <= pendingUser.baseCount;
  const displayMessages = showPending ? [...(messages || []), pendingUser] : messages;
  const { data: contextUsage } = useContextUsage(selectedConversationId);
  const { data: spend } = useConversationSpend(selectedConversationId, contextUsage?.estimated_tokens);
  const setContextUsage = useSetContextUsage();
  const updateProject = useUpdateProject();
  const { hasNewVersion, reload } = useVersion();

  const currentProject = projects?.find((p) => p.id === selectedProjectId);
  const isGeneral = currentProject?.kind === "general"; // el agente general («🧠 Orquestador»): sin servidores ni vista previa propios
  const viewingDeleted = (deletedConversations || []).find((c) => c.id === selectedConversationId) || null; // un chat eliminado se ve en solo lectura
  const viewingParentDeleted = !!viewingDeleted?.parent_conversation_id && (deletedConversations || []).some((c) => c.id === viewingDeleted.parent_conversation_id);
  const { data: serverStatus } = useServerStatus(selectedProjectId);
  const { data: readiness } = useReadiness(selectedProjectId); // servidores que los documentos mencionan y aún no tienen credenciales
  const pendingServers = readiness?.pending || [];

  // Si se abre OTRA conversación, el aviso de «trabajando» de la anterior no se queda pegado (la tarea sigue en su conversación).
  useEffect(() => {
    if (jobRunning && jobConvRef.current !== selectedConversationId) setJobRunning(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversationId]);

  const handleSelectProject = (id) => {
    setSelectedConversationId(null);
    navigate(`/chat/${id}`);
  };

  const handleNewConversation = () => setSelectedConversationId(null);

  // Si la respuesta tarda y la conexión se corta, la respuesta igual queda guardada en el servidor: se sigue buscando.
  const recoverAfterFailure = async () => {
    setSendError(true);
    const list = (await refetchConversations()).data;
    if (mountedRef.current && !selectedConversationId && list?.length) setSelectedConversationId(list[0].id);
  };

  const handleSend = async (text, attachmentIds = [], forcedConversationId = null) => {
    const targetConversationId = forcedConversationId || selectedConversationId; // el inicio manda el mensaje al chat principal del agente general
    if (forcedConversationId) {
      setSelectedConversationId(forcedConversationId);
      try {
        sentCountRef.current = ((await apiClient.get(`/conversations/${forcedConversationId}/messages`)).data || []).length;
      } catch (err) {
        sentCountRef.current = 0;
      }
    } else {
      sentCountRef.current = messages?.length || 0;
    }
    setSendError(false);
    const rid = newRequestId();
    setRequestId(rid);
    setStartedAt(Date.now());
    // Un texto con pinta de credenciales NO se muestra de inmediato: el panel lo oculta al guardarlo.
    setPendingUser(
      looksLikeCredentials(text)
        ? null
        : { id: `pending-${rid}`, role: "user", content: text, attachments: [], conversationId: targetConversationId, baseCount: sentCountRef.current }
    );
    let result;
    try {
      result = await sendMessage.mutateAsync({
        projectId: selectedProjectId,
        message: text,
        conversationId: targetConversationId,
        attachmentIds,
        requestId: rid,
      });
    } catch (err) {
      if (mountedRef.current) await recoverAfterFailure();
      return;
    }
    if (!mountedRef.current) {
      // Se cambió de proyecto mientras respondía: la respuesta ya quedó guardada en SU proyecto; aquí no se muestra.
      queryClient.invalidateQueries({ queryKey: ["conversations", selectedProjectId] });
      return;
    }
    setSelectedConversationId(result.conversation_id);
    queryClient.invalidateQueries({ queryKey: ["conversations", selectedProjectId] }); // un chat nuevo pasa a ser el principal
    if (result.running) {
      // La tarea es larga: sigue en segundo plano. Se muestra el avance (pasos y plan) hasta que aparezca la respuesta.
      jobConvRef.current = result.conversation_id;
      setJobRunning(true);
      setPendingUser((cur) => (cur ? { ...cur, conversationId: result.conversation_id } : cur));
      return;
    }
    setPendingUser((cur) => (cur ? { ...cur, conversationId: result.conversation_id } : cur));
    // Si el mensaje traía credenciales, el panel pudo haber guardado un servidor: se refresca la lista de «Servidores».
    queryClient.invalidateQueries({ queryKey: ["servers", selectedProjectId] });
    queryClient.invalidateQueries({ queryKey: ["messages", result.conversation_id] });
    setContextUsage(result.conversation_id, result.context_usage);
  };

  // Mensaje automático que llega desde "Documentar en Drive" (análisis de servidor): se envía UNA vez.
  const location = useLocation();
  const autoSent = useRef(false);
  useEffect(() => {
    const autoMessage = location.state?.autoMessage;
    if (!autoMessage || !selectedProjectId || autoSent.current || sendMessage.isPending) return;
    autoSent.current = true;
    navigate(location.pathname, { replace: true, state: null });
    handleSend(autoMessage, [], location.state?.conversationId || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, selectedProjectId]);

  // Al abrir el proyecto se abre su chat principal (el primero, o el que elegiste). Sin chats, se ve la bienvenida. «Nueva conversación» sigue abriendo una en blanco.
  const autoOpened = useRef(false);
  useEffect(() => {
    if (autoOpened.current || conversations === undefined) return;
    autoOpened.current = true;
    if (location.state?.autoMessage || autoSent.current || sendMessage.isPending) return; // un mensaje automático abre su propio chat
    const main = conversations.find((c) => c.is_main);
    if (main) setSelectedConversationId((current) => current || main.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations]);

  useEffect(() => {
    const last = messages?.[messages.length - 1];
    if (waiting && last?.role === "assistant" && messages.length >= sentCountRef.current + 2) {
      setSendError(false);
      if (jobRunning) {
        setJobRunning(false);
        queryClient.invalidateQueries({ queryKey: ["context-usage", selectedConversationId] });
      }
      if (sendMessage.isPending) sendMessage.reset(); // la respuesta ya está: no se queda esperando una conexión muerta
      queryClient.invalidateQueries({ queryKey: ["conversations", selectedProjectId] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const handleCancel = async () => {
    try {
      await cancelJob(requestId);
    } catch (err) {
      // si ya terminó, no hay nada que detener
    }
  };

  const handleRefresh = async () => {
    await refetchConversations();
    if (selectedConversationId) await refetchMessages();
  };

  const handleTopicCreated = (topicId) => {
    queryClient.invalidateQueries({ queryKey: ["conversations", selectedProjectId] });
    setSelectedConversationId(topicId);
  };

  const handleCompacted = (usage) => {
    queryClient.invalidateQueries({ queryKey: ["messages", selectedConversationId] });
    setContextUsage(selectedConversationId, usage);
  };

  const handleModelChange = (model) => {
    if (!selectedProjectId) return;
    updateProject.mutate({ id: selectedProjectId, model });
  };

  // Si lo que estaba abierto se eliminó, se abre el chat principal (el nuevo, si se acaba de elegir) o, sin chats, la bienvenida.
  const openAfterRemoval = (removedIds, preferredId) => {
    if (!removedIds.includes(selectedConversationId)) return;
    setSelectedConversationId(preferredId || (conversations || []).find((c) => c.is_main && !removedIds.includes(c.id))?.id || null);
  };

  const confirmDelete = async ({ newMainId }) => {
    const target = deleteTarget;
    const removed = [target.id, ...(conversations || []).filter((c) => c.parent_conversation_id === target.id).map((c) => c.id)];
    setDialogError("");
    try {
      const result = await trashConversation.mutateAsync({ id: target.id, newMainId });
      setDeleteTarget(null);
      openAfterRemoval(removed, (result?.was_main ? result.main_conversation_id : null) || (target.kind === "tema" ? target.parent_conversation_id : null));
    } catch (e) {
      setDialogError(errorMessage(e, "No se pudo eliminar el chat."));
    }
  };

  const confirmPurge = async () => {
    const target = purgeTarget;
    const removed = [target.id, ...(deletedConversations || []).filter((c) => c.parent_conversation_id === target.id).map((c) => c.id)];
    setDialogError("");
    try {
      await purgeConversation.mutateAsync(target.id);
      setPurgeTarget(null);
      openAfterRemoval(removed, null);
    } catch (e) {
      setDialogError(errorMessage(e, "No se pudo eliminar definitivamente el chat."));
    }
  };

  const handleMakeMain = async (conversation) => {
    setChatsError("");
    try {
      await makeMain.mutateAsync(conversation.id);
    } catch (e) {
      setChatsError(errorMessage(e, "No se pudo dejar ese chat como principal."));
    }
  };

  const handleRestore = async (conversation) => {
    setChatsError("");
    try {
      await restoreConversation.mutateAsync(conversation.id);
    } catch (e) {
      setChatsError(errorMessage(e, "No se pudo restaurar el chat."));
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <AppShell
      projects={projects}
      selectedProjectId={selectedProjectId}
      onSelectProject={handleSelectProject}
      onGoHome={() => navigate("/")}
      modelValue={currentProject?.model}
      onModelChange={handleModelChange}
      onLogout={handleLogout}
      versionBannerProps={{ visible: hasNewVersion, onReload: reload }}
      sidebar={
        <ConversationList
          conversations={conversations}
          deletedConversations={deletedConversations}
          selectedConversationId={selectedConversationId}
          onSelect={setSelectedConversationId}
          onNew={handleNewConversation}
          onMakeMain={handleMakeMain}
          onDelete={(conversation) => { setDialogError(""); setDeleteTarget(conversation); }}
          onRestore={handleRestore}
          onPurge={(conversation) => { setDialogError(""); setPurgeTarget(conversation); }}
        />
      }
    >
      {selectedConversationId && !viewingDeleted && (
        <div className="border-b border-border px-4 py-2 flex items-center justify-between shrink-0 gap-3">
          <ContextUsageBadge
            usage={contextUsage}
            spend={spend}
            onCompact={() => setShowCompactDialog(true)}
            onSuggestTopic={() => setShowTopicDialog(true)}
          />
          <div className="flex items-center gap-2 shrink-0">
            <PlanButton conversationId={selectedConversationId} projectId={selectedProjectId} onOpenConversation={setSelectedConversationId} watch={waiting} />
            {isGeneral ? (
              <Button data-testid="general-prompt-button" size="sm" variant="outline" onClick={() => setShowGeneralPrompt(true)}>
                <SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" /> Instrucciones del agente
              </Button>
            ) : (
              <>
                <PreviewButton projectId={currentProject?.id} />
                <DeployButton projectId={currentProject?.id} />
                <Button
                  data-testid="servers-button"
                  size="sm"
                  variant="outline"
                  onClick={() => setShowServersDialog(true)}
                >
                  <Server className="h-3.5 w-3.5 mr-1.5" /> Servidores
                </Button>
              </>
            )}
            <Button data-testid="memory-button" size="sm" variant="outline" onClick={() => setShowMemory(true)}>
              <Brain className="h-3.5 w-3.5 mr-1.5" /> Memoria
            </Button>
            <Button
              data-testid="compact-chat-button"
              size="sm"
              variant="outline"
              onClick={() => setShowCompactDialog(true)}
            >
              <Minimize2 className="h-3.5 w-3.5 mr-1.5" /> Compactar chat
            </Button>
            <Button
              data-testid="new-topic-button"
              size="sm"
              variant="outline"
              onClick={() => setShowTopicDialog(true)}
            >
              <GitBranch className="h-3.5 w-3.5 mr-1.5" /> Nuevo tema
            </Button>
          </div>
        </div>
      )}
      {viewingDeleted && (
        <div data-testid="deleted-chat-banner" className="shrink-0 border-b border-amber-500/40 px-4 py-2 flex items-center justify-between gap-3 text-sm">
          <span className="text-muted-foreground">
            🗑️ Este chat está en «Chats eliminados» (solo lectura). {viewingParentDeleted ? "Se restaura junto con su chat de origen." : "Restáuralo para seguir escribiendo en él."}
          </span>
          <Button data-testid="deleted-chat-restore" size="sm" variant="outline" className="shrink-0" disabled={viewingParentDeleted || restoreConversation.isPending} onClick={() => handleRestore(viewingDeleted)}>
            Restaurar
          </Button>
        </div>
      )}
      {chatsError && (
        <div data-testid="chats-error" className="shrink-0 border-b border-amber-500/40 px-4 py-2 flex items-center justify-between gap-3 text-sm text-amber-500">
          <span>{chatsError}</span>
          <Button data-testid="chats-error-close" size="sm" variant="ghost" className="shrink-0" onClick={() => setChatsError("")}>Cerrar</Button>
        </div>
      )}
      {pendingServers.length > 0 && (
        <div data-testid="pending-credentials-banner" className="shrink-0 border-b border-amber-500/40 px-4 py-2 flex items-center justify-between gap-3 text-sm">
          <span className="text-muted-foreground">
            Faltan credenciales de: <span className="font-medium text-foreground">{pendingServers.map((p) => p.name).join(", ")}</span>. Pégalas con el
            candado y quedan conectadas y analizadas solas.
          </span>
          <Button data-testid="pending-credentials-button" size="sm" variant="outline" className="shrink-0" onClick={() => setShowCredentialsBox(true)}>
            <Lock className="h-3.5 w-3.5 mr-1.5" /> Pegar credenciales
          </Button>
        </div>
      )}
      <ChatWindow
        messages={displayMessages}
        steps={progress?.steps}
        plan={progress?.plan}
        startedAt={startedAt}
        onCancel={requestId ? handleCancel : undefined}
        isSending={sendMessage.isPending || jobRunning}
        onSendMessage={handleSend}
        emptyState={
          !selectedConversationId && isGeneral ? (
            <div data-testid="general-welcome" className="text-center max-w-md space-y-2">
              <p className="text-lg font-semibold">🧠 Orquestador</p>
              <p className="text-sm text-muted-foreground">
                Pregúntame por cualquiera de tus proyectos: los reviso en solo lectura, te sugiero qué hacer y te ayudo a diseñar los nuevos.
              </p>
            </div>
          ) : !selectedConversationId && currentProject ? (
            <ProjectWelcome
              project={currentProject}
              hasServer={serverStatus?.has_server}
              onManageServers={() => setShowServersDialog(true)}
              onSend={handleSend}
              disabled={sendMessage.isPending || jobRunning}
            />
          ) : undefined
        }
      />
      {sendError && (
        <div data-testid="send-error-banner" className="shrink-0 border-t border-border px-4 py-2 flex items-center justify-between gap-3 text-sm">
          <span className="text-muted-foreground">
            La conexión se cortó o la respuesta tardó. Sigo revisando si ya está lista; puedes pulsar Actualizar.
          </span>
          <Button data-testid="send-error-refresh" size="sm" variant="outline" onClick={handleRefresh}>
            Actualizar
          </Button>
        </div>
      )}
      {!viewingDeleted && (
        <MessageInput
          onSend={handleSend}
          disabled={!selectedProjectId || sendMessage.isPending || jobRunning}
          onOpenCredentials={selectedProjectId ? () => setShowCredentialsBox(true) : undefined}
        />
      )}
      <DeleteConversationDialog
        conversation={deleteTarget}
        conversations={conversations}
        busy={trashConversation.isPending}
        error={dialogError}
        onConfirm={confirmDelete}
        onCancel={() => { setDeleteTarget(null); setDialogError(""); }}
      />
      <PurgeConversationDialog
        conversation={purgeTarget}
        busy={purgeConversation.isPending}
        error={dialogError}
        onConfirm={confirmPurge}
        onCancel={() => { setPurgeTarget(null); setDialogError(""); }}
      />
      <NewTopicDialog
        open={showTopicDialog}
        onOpenChange={setShowTopicDialog}
        parentConversationId={selectedConversationId}
        projectId={selectedProjectId}
        onTopicCreated={handleTopicCreated}
      />
      <ServersDialog open={showServersDialog} onOpenChange={setShowServersDialog} project={currentProject} />
      <CredentialsBoxDialog open={showCredentialsBox} onOpenChange={setShowCredentialsBox} project={currentProject} />
      <MemoryDialog open={showMemory} onOpenChange={setShowMemory} project={currentProject} />
      {isGeneral && <GeneralPromptDialog open={showGeneralPrompt} onOpenChange={setShowGeneralPrompt} project={currentProject} />}
      <PreviewHost />
      <DeployHost projectId={currentProject?.id} />
      <CompactChatDialog
        open={showCompactDialog}
        onOpenChange={setShowCompactDialog}
        conversationId={selectedConversationId}
        onCompacted={handleCompacted}
      />
    </AppShell>
  );
}
